import { FC } from 'react';
import { Text, View } from '@react-pdf/renderer';

import Table from '../PdfTable';
import styles from './styles';

interface GainsProps {
  taxYear: string;
  gains: Array<{
    title: string;
    date: string;
    capitalAllocated: string;
    sharesSold: number;
    saleValue: string;
    isGain: boolean;
    formattedValue: string;
    value: number;
    isTaxReliefElegible: boolean;
  }>;
  totals: {
    elegible: string;
    other: string;
  };
}

const Gains: FC<GainsProps> = ({ taxYear, gains, totals }) => {
  if (!gains.length) {
    return (
      <View wrap={false}>
        <Text style={styles.heading}>Realised gains</Text>
        <Text style={styles.paragraph}>
          You did not receive any capital gains from realisations during the{' '}
          {taxYear} tax year.
        </Text>
      </View>
    );
  }

  return (
    <>
      <View wrap={false}>
        <Text style={styles.heading}>Realised gains</Text>
        <Text style={styles.paragraph}>
          You made the following capital gains from realisations during this tax
          year:
        </Text>
      </View>

      <Table
        title={null}
        backgroundColor="white"
        fontColor="#333333"
        alignContent="left"
        fontSize={8}
        fullBorder
        expandRows
        columns={{
          investment: { value: 'Investment', bold: true },
          investmentDate: {
            value: 'Realisation date',
            bold: true,
            width: 12,
          },
          shares: {
            value: 'Shares/units realised',
            bold: true,
            width: 12,
          },
          capitaAllocated: {
            value: 'Initial purchase value',
            bold: true,
            width: 15,
          },
          saleValue: {
            value: 'Sale proceeds (before fees)',
            bold: true,
            width: 15,
          },
          elegibleValue: {
            value: 'S/EIS eligible gain',
            bold: true,
            width: 15,
          },
          otherValue: {
            value: 'Other gain',
            bold: true,
            width: 15,
          },
        }}
        data={[
          ...gains.map((gain) => ({
            investment: {
              value: gain.title,
            },
            investmentDate: {
              value: gain.date,
              width: 12,
            },
            shares: {
              value: gain.sharesSold,
              width: 12,
            },
            capitaAllocated: {
              value: gain.capitalAllocated,
              width: 15,
            },
            saleValue: {
              value: gain.saleValue,
              width: 15,
            },
            elegibleValue: {
              value: gain.isTaxReliefElegible ? gain.formattedValue : '',
              width: 15,
            },
            otherValue: {
              value: !gain.isTaxReliefElegible ? gain.formattedValue : '',
              width: 15,
            },
          })),
        ]}
        footer={{
          investment: {
            value: '',
          },
          investmentDate: {
            value: '',
            width: 12,
          },
          shares: {
            value: '',
            width: 12,
          },
          capitaAllocated: {
            value: '',
            width: 15,
          },
          saleValue: {
            value: '',
            width: 15,
          },
          elegibleValue: {
            value: totals.elegible,
            bold: true,
            width: 15,
          },
          otherValue: {
            value: totals.other,
            bold: true,
            width: 15,
          },
        }}
      />
      <Text style={[styles.paragraph, styles.topMargin]}>
        S/EIS eligible capital gains are exempt from UK capital gains tax and do
        not need to be declared on a UK tax return. Other gains are taxable and
        must be declared to HMRC if you are a UK tax resident.
      </Text>
      <Text style={styles.paragraph}>
        Where an earnout or escrow payment is received, the{' '}
        <Text style={styles.italic}>Initial purchase value</Text> field will
        display the aggregate exit value received to date, to allow you to
        easily review the new additional payment you have received.
      </Text>
      <Text style={styles.paragraph}>
        If you pay tax in a non-UK jurisdiction, please seek professional tax
        advice before deciding which of the above capital gains are subject to
        tax in your applicable jurisdiction.
      </Text>
    </>
  );
};

export default Gains;
